/**
 * Extension State Service
 * 
 * Centralized service for managing extension detection, capabilities,
 * and state synchronization across all UI components.
 * 
 * This ensures consistent extension awareness across:
 * - file-transfer-drawer.tsx
 * - extension-enhanced-drawer.tsx  
 * - AttachmentCard (chat-message-card.tsx)
 * - chat-panel.tsx
 */

import { extensionCapabilityService, TRANSFER_THRESHOLDS } from '../services/extension-capability-service';

export interface ExtensionCapabilities {
  maxFileSize: number;
  hasEnhancedStorage: boolean;
  hasNativeFileSystem: boolean;
  hasWorkerPool: boolean;
  version: string;
}

export interface ExtensionState {
  hasExtension: boolean;
  isChromium: boolean;
  isEdge: boolean;
  capabilities: ExtensionCapabilities | null;
  isInitialized: boolean;
}

// Global extension state
let extensionState: ExtensionState = {
  hasExtension: false,
  isChromium: false,
  isEdge: false,
  capabilities: null,
  isInitialized: false,
};

// Extension state change listeners
const listeners = new Set<(state: ExtensionState) => void>();

/**
 * Detect browser type with enhanced Edge support (safe for SSR)
 */
function detectBrowserType(): { isChromium: boolean; isEdge: boolean } {
  // Safe browser detection that won't crash during initialization
  try {
    const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    
    // Detect Edge (including Chromium-based Edge)
    const isEdge = /Edge|Edg\//.test(userAgent);
    
    // Detect Chromium-based browsers safely
    const hasWindowChrome = typeof window !== 'undefined' && 
                           'chrome' in window && 
                           !!(window as any).chrome;
    
    const isChromium = hasWindowChrome ||
                       /Chrome|Chromium/.test(userAgent) ||
                       isEdge; // Edge is Chromium-based
    
    return { isChromium, isEdge };
  } catch (error) {
    console.warn('[ExtensionState] Browser detection failed, using defaults:', error);
    return { isChromium: false, isEdge: false };
  }
}

/**
 * Detect extension availability and capabilities
 */
function detectExtensionCapabilities(): ExtensionCapabilities | null {
  const extension = (window as any).mobyNetExtension;
  
  if (!extension?.isAvailable) {
    return null;
  }
  
  try {
    const capabilities = extension.getCapabilities();
    
    // Validate capabilities structure
    if (typeof capabilities === 'object' && capabilities !== null) {
      return {
        maxFileSize: capabilities.maxFileSize || 100 * 1024 * 1024, // 100MB default
        hasEnhancedStorage: !!capabilities.hasEnhancedStorage,
        hasNativeFileSystem: !!capabilities.hasNativeFileSystem,
        hasWorkerPool: !!capabilities.hasWorkerPool,
        version: capabilities.version || 'unknown',
      };
    }
  } catch (error) {
    console.warn('[ExtensionState] Failed to get extension capabilities:', error);
  }
  
  return null;
}

/**
 * Initialize extension state detection
 */
export function initializeExtensionState(): ExtensionState {
  if (extensionState.isInitialized) {
    return extensionState;
  }
  
  const { isChromium, isEdge } = detectBrowserType();
  const capabilities = detectExtensionCapabilities();
  const hasExtension = capabilities !== null;
  
  extensionState = {
    hasExtension,
    isChromium,
    isEdge,
    capabilities,
    isInitialized: true,
  };
  
  console.log('[ExtensionState] Initialized:', {
    hasExtension,
    isChromium,
    isEdge,
    capabilities: capabilities ? 'available' : 'none',
  });
  
  // Notify all listeners
  notifyListeners();
  
  return extensionState;
}

/**
 * Get current extension state (initializes if needed)
 */
export function getExtensionState(): ExtensionState {
  if (!extensionState.isInitialized) {
    return initializeExtensionState();
  }
  
  return extensionState;
}

/**
 * Subscribe to extension state changes
 */
export function subscribeToExtensionState(listener: (state: ExtensionState) => void): () => void {
  listeners.add(listener);
  
  // Immediately notify with current state
  listener(getExtensionState());
  
  // Return unsubscribe function
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Force refresh extension state (useful for dynamic extension installation)
 */
export function refreshExtensionState(): ExtensionState {
  extensionState.isInitialized = false;
  return initializeExtensionState();
}

/**
 * Notify all listeners of state changes
 */
function notifyListeners(): void {
  listeners.forEach(listener => {
    try {
      listener(extensionState);
    } catch (error) {
      console.error('[ExtensionState] Listener error:', error);
    }
  });
}

/**
 * Check if current browser supports extension enhancements
 */
export function isBrowserSupported(): boolean {
  const state = getExtensionState();
  return state.isChromium || state.isEdge;
}

/**
 * Check if extension is fully functional
 */
export function isExtensionFullyFunctional(): boolean {
  const state = getExtensionState();
  return state.hasExtension && 
         state.capabilities !== null && 
         (state.isChromium || state.isEdge);
}

/**
 * Get maximum supported file size based on extension/browser capabilities
 */
export async function getMaxSupportedFileSize(): Promise<number> {
  const state = getExtensionState();
  
  if (state.hasExtension && state.capabilities) {
    return state.capabilities.maxFileSize;
  }
  
  // Use dynamic OPFS limit for browser-only mode
  try {
    const thresholds = await extensionCapabilityService.getDynamicThresholds();
    return thresholds.OPFS_LIMIT;
  } catch {
    // Conservative static fallback
    return TRANSFER_THRESHOLDS.OPFS_LIMIT;
  }
}
